import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { getStores } from '../../services/customerApi';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { motion, AnimatePresence } from 'framer-motion';
import { House, MagnifyingGlass, ClockCounterClockwise, User, ShoppingCart, DownloadSimple, X } from '@phosphor-icons/react';
import CustomerLogin from './CustomerLogin';
import FloatingHelpButton from '@/components/Customer/FloatingHelpButton';
import CustomerAuthCallback from './CustomerAuthCallback';
import CustomerPhoneSetup from './CustomerPhoneSetup';
import CustomerProfileSetup from './CustomerProfileSetup';
import CustomerHome from './CustomerHome';
import CustomerMenu from './CustomerMenu';
import StoreSelect from './StoreSelect';
import CustomerCart from './CustomerCart';
import CustomerCheckout from './CustomerCheckout';
import CustomerTrack from './CustomerTrack';
import CustomerOrders from './CustomerOrders';
import CustomerProfile from './CustomerProfile';
import CustomerWallet from './CustomerWallet';
import * as engine from '@/lib/ordering/cartEngine';

// Customer context for shared state
export const CustomerContext = createContext(null);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Synchronous, crash-safe read of persisted JSON. Corrupted values are purged
// so a bad payload can never wedge the app on reload.
function readPersisted(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

export const useCustomer = () => useContext(CustomerContext);

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.2 } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.15 } }
};

export function PageWrap({ children }) {
  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit">
      {children}
    </motion.div>
  );
}

function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart } = useCustomer();
  const cartCount = cart.reduce((sum, item) => sum + item.qty, 0);

  const tabs = [
    { path: '/order', icon: House, label: 'Home' },
    { path: '/order/menu', icon: MagnifyingGlass, label: 'Menu' },
    { path: '/order/cart', icon: ShoppingCart, label: 'Cart', badge: cartCount },
    { path: '/order/orders', icon: ClockCounterClockwise, label: 'Orders' },
    { path: '/order/profile', icon: User, label: 'Profile' },
  ];

  const isActive = (path) => {
    if (path === '/order') return location.pathname === '/order';
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      data-testid="bottom-nav"
      className="fixed bottom-0 left-0 right-0 z-50 flex justify-center pb-4 px-4 pointer-events-none"
    >
      <div className="flex items-center justify-around w-full max-w-md bg-white/95 backdrop-blur-xl border border-gray-100 rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.06)] py-2 px-2 pointer-events-auto">
        {tabs.map((tab) => {
          const active = isActive(tab.path);
          const Icon = tab.icon;
          return (
            <button
              key={tab.path}
              data-testid={`nav-${tab.label.toLowerCase()}`}
              onClick={() => navigate(tab.path)}
              className={`relative flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-full transition-all duration-200 ${
                active ? 'text-sspice' : 'text-gray-400'
              }`}
            >
              <Icon size={22} weight={active ? 'fill' : 'regular'} />
              <span className="text-[10px] font-medium">{tab.label}</span>
              {tab.badge > 0 && (
                <span className="absolute -top-1 right-0 bg-sspice text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {tab.badge > 9 ? '9+' : tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export default function CustomerApp() {
  // Hydrate synchronously from localStorage on first render — no flicker, no race.
  const [customer, setCustomer] = useState(() => {
    const token = localStorage.getItem('customer_token');
    const user = readPersisted('customer_user');
    return token && user ? user : null;
  });
  const [selectedStore, setSelectedStore] = useState(() => readPersisted('customer_store'));
  const [cart, setCart] = useState(() => {
    const saved = readPersisted('customer_cart');
    return Array.isArray(saved) ? saved : [];
  });

  const didMountCart = useRef(false);

  // Persist cart on change, but skip the very first commit so the freshly
  // hydrated cart is never clobbered with an empty array during initial render.
  useEffect(() => {
    if (!didMountCart.current) {
      didMountCart.current = true;
      return;
    }
    if (cart.length === 0) {
      localStorage.removeItem('customer_cart');
    } else {
      localStorage.setItem('customer_cart', JSON.stringify(cart));
    }
  }, [cart]);

  // Validate the persisted store still exists. Never silently fall back to a
  // different/default store — if it's gone, clear it and let routing prompt a
  // fresh selection.
  useEffect(() => {
    if (!selectedStore?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getStores();
        const stores = res.data.stores || [];
        const match = stores.find((s) => s.id === selectedStore.id);
        if (cancelled) return;
        if (!match) {
          localStorage.removeItem('customer_store');
          setSelectedStore(null);
        } else {
          // Refresh persisted copy with latest store data.
          localStorage.setItem('customer_store', JSON.stringify(match));
          setSelectedStore(match);
        }
      } catch {
        /* network hiccup — keep the persisted store, don't wipe it */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loginCustomer = (token, user) => {
    localStorage.setItem('customer_token', token);
    localStorage.setItem('customer_user', JSON.stringify(user));
    setCustomer(user);
  };

  const logoutCustomer = () => {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_user');
    localStorage.removeItem('customer_store');
    localStorage.removeItem('customer_cart');
    setCustomer(null);
    setSelectedStore(null);
    setCart([]);
  };

  const selectStore = (store) => {
    localStorage.setItem('customer_store', JSON.stringify(store));
    setSelectedStore(store);
    setCart([]);
    localStorage.removeItem('customer_cart');
  };

  const addToCart = (cartItem) => {
    // cartItem must be built via engine.createCartItem (carries a signature)
    setCart((prev) => engine.addItem(prev, cartItem));
  };

  const updateCartQty = (signature, qty) => {
    setCart((prev) => engine.updateQty(prev, signature, qty));
  };

  const removeCartItem = (signature) => {
    setCart((prev) => engine.removeItem(prev, signature));
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('customer_cart');
  };

  const contextValue = {
    customer, selectedStore, cart,
    loginCustomer, logoutCustomer, selectStore,
    addToCart, updateCartQty, removeCartItem, clearCart
  };

  // PWA Install Prompt
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      const dismissed = localStorage.getItem('pwa_install_dismissed');
      if (!dismissed) setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowInstallBanner(false);
      setInstallPrompt(null);
    }
  };

  const dismissInstall = () => {
    setShowInstallBanner(false);
    localStorage.setItem('pwa_install_dismissed', 'true');
  };

  const isLoggedIn = !!customer;
  const hasStore = !!selectedStore;

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <CustomerContext.Provider value={contextValue}>
      <div className="min-h-screen bg-white font-['Work_Sans',sans-serif]" data-testid="customer-app">
        {/* Store Closed Banner — shown whenever staff has manually stopped operations */}
        {selectedStore && selectedStore.accepting_orders === false && (
          <div
            className="sticky top-0 z-50 bg-red-600 text-white px-4 py-2.5 text-center shadow-md"
            data-testid="store-closed-banner"
          >
            <p className="text-xs font-semibold">
              🔴 We're temporarily closed. Due to unforeseen circumstances, we're not accepting new orders right now.
              If you have an order in progress, our team will call you shortly. We're sorry for the inconvenience — we'll be back soon!
            </p>
          </div>
        )}

        {/* PWA Install Banner */}
        <AnimatePresence>
          {showInstallBanner && (
            <motion.div
              initial={{ y: -60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -60, opacity: 0 }}
              className="fixed top-0 left-0 right-0 z-[60] bg-[#2D231E] text-white px-4 py-3 flex items-center gap-3 shadow-lg"
              data-testid="pwa-install-banner"
            >
              <DownloadSimple size={20} weight="bold" className="text-[#E21836] flex-shrink-0" />
              <p className="flex-1 text-xs font-medium">Install SSPICE Pizza for a faster experience</p>
              <button
                data-testid="pwa-install-btn"
                onClick={handleInstall}
                className="px-3 py-1.5 bg-[#E21836] text-white text-xs font-semibold rounded-full hover:bg-[#C8122D] transition-colors"
              >
                Install
              </button>
              <button
                data-testid="pwa-dismiss-btn"
                onClick={dismissInstall}
                className="p-1 text-white/50 hover:text-white"
              >
                <X size={16} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence mode="wait">
          <Routes>
            <Route path="/login" element={isLoggedIn ? <Navigate to="/order" /> : <CustomerLogin />} />
            <Route path="/auth/callback" element={<CustomerAuthCallback />} />
            <Route path="/setup-phone" element={isLoggedIn ? <CustomerPhoneSetup /> : <Navigate to="/order/login" />} />
            <Route path="/setup-profile" element={isLoggedIn ? <CustomerProfileSetup /> : <Navigate to="/order/login" />} />
            <Route path="/select-store" element={isLoggedIn ? <StoreSelect /> : <Navigate to="/order/login" />} />
            <Route path="/" element={isLoggedIn ? (hasStore ? <CustomerHome /> : <Navigate to="/order/select-store" />) : <Navigate to="/order/login" />} />
            <Route path="/menu" element={isLoggedIn ? (hasStore ? <CustomerMenu /> : <Navigate to="/order/select-store" />) : <Navigate to="/order/login" />} />
            <Route path="/menu/:storeId" element={isLoggedIn ? <CustomerMenu /> : <Navigate to="/order/login" />} />
            <Route path="/cart" element={isLoggedIn ? <CustomerCart /> : <Navigate to="/order/login" />} />
            <Route path="/checkout" element={isLoggedIn ? <CustomerCheckout /> : <Navigate to="/order/login" />} />
            <Route path="/track/:orderId" element={<CustomerTrack />} />
            <Route path="/orders" element={isLoggedIn ? <CustomerOrders /> : <Navigate to="/order/login" />} />
            <Route path="/profile" element={isLoggedIn ? <CustomerProfile /> : <Navigate to="/order/login" />} />
            <Route path="/wallet" element={isLoggedIn ? <CustomerWallet /> : <Navigate to="/order/login" />} />
          </Routes>
        </AnimatePresence>
        {isLoggedIn && <BottomNav />}
        <FloatingHelpButton />
      </div>
    </CustomerContext.Provider>
    </GoogleOAuthProvider>
  );
}
